import { useMemo, forwardRef } from 'react';
import {
  LinkProps as AriaLinkProps,
  Link as AriaLink,
} from 'react-aria-components';
import { useObjectRef } from '@react-aria/utils';
import VisuallyHidden from '../VisuallyHidden/VisuallyHidden';
import { Icon, IconProps, IconSize } from '../Icon/Icon';
import { cva } from 'class-variance-authority';
import styles from './Link.module.css';
import { focusStyleVariants } from '../../utils/focus/focusStyles';
import {
  foregroundColorVariants,
  type ForegroundVariants,
} from '../../utils/foregroundColour/foregroundColor';
import {
  generateResponsiveClasses,
  getBaseValue,
  WithResponsiveProps,
} from '../../utils/breakpoint/responsiveSSR';
import { altoConsole } from '../../utils/console/console';

const getResponsiveClasses = generateResponsiveClasses(styles);

const link = cva(styles.link, {
  variants: {
    size: {
      xs: styles['size--xs'],
      sm: styles['size--sm'],
      md: styles['size--md'],
      lg: styles['size--lg'],
    },
    underline: {
      true: styles['link--underline'],
      false: '',
    },
    iconEnd: {
      true: styles['link__icon-end'],
      false: '',
    },
    iconStart: {
      true: styles['link__icon-start'],
      false: '',
    },
    inline: {
      true: styles['link--inline'],
      false: '',
    },
  },
  defaultVariants: {
    size: 'md',
    underline: false,
  },
});

export interface LinkBaseProps extends AriaLinkProps {
  /**
   * The children of the component. A function may be provided to alter the children based on component state.
   */
  children?: React.ReactNode | string;
  /**
   * What the size of the Link is
   * @default 'md'
   */
  size?: 'xs' | 'sm' | 'md' | 'lg';
  /**
   * Whether the Link should be underlined
   * @default false
   */
  underline?: boolean;
  /**
   * Allow Link to inherit font family, weight, size and color from parent element
   * @default false
   */
  inline?: boolean;
  /**
   * Whether the link is disabled.
   * @default false
   */
  isDisabled?: boolean;
  /**
   * The foreground color of the label
   */
  foregroundColor?: ForegroundVariants['foregroundColor'];
  /**
   * Whether an icon should display at the end of Link and icon properties. The default right arrow is not rendered when the Link is `inline`
   * @default { iconName: 'arrow-right' }
   */
  iconEndProps?: IconProps | false;
  /**
   * Whether an icon should display at the start of the Link and icon properties
   */
  iconStartProps?: IconProps;
  /**
   * The URL the Link will navigate to
   */
  href?: string;
  /**
   * The target attribute for the Link
   * @default '_self'
   */
  target?: string;
  /**
   * @deprecated Use externalLinkScreenReaderText instead
   * Aria label applied when target="_blank"
   * @default "open in new tab"
   */
  ariaLabelExternal?: string;
  /**
   * Specify custom screen reader text for external links
   * @default "opens in a new tab"
   */
  externalLinkScreenReaderText?: string;
  /**
   * 	The focus style of the button
   */
  focusStyle?: 'default' | 'white';
}

type ResponsivePropKeys = 'size';

export type LinkProps = WithResponsiveProps<LinkBaseProps, ResponsivePropKeys>;

const Link = forwardRef<HTMLAnchorElement, LinkProps>(function Link(
  {
    size = 'md',
    underline = true,
    inline = false,
    iconStartProps,
    iconEndProps: iconEndPropsDefault,
    href,
    target = '_self',
    focusStyle = 'default',
    children,
    ariaLabelExternal,
    externalLinkScreenReaderText = 'opens in a new tab',
    foregroundColor = 'accentPrimary',
    ...props
  },
  forwardedRef,
) {
  const anchorRef = useObjectRef(forwardedRef);

  // Render
  const iconEndProps =
    iconEndPropsDefault === undefined && !inline
      ? { iconName: 'arrow-right' }
      : iconEndPropsDefault;

  // Deprecation warning
  if (ariaLabelExternal) {
    altoConsole.warn(
      '`ariaLabelExternal` prop is deprecated. Use the `externalLinkScreenReaderText` prop instead.',
      { scope: 'Link' },
    );
  }

  const baseSize = getBaseValue(size, 'md');

  const sizeResponsiveClasses = getResponsiveClasses(size, 'size');

  const isExternalLink = target === '_blank';

  const iconSize = useMemo<IconSize>(() => {
    switch (size) {
      case 'xs':
        return '0.75x';
      case 'sm':
        return '0.75x';
      case 'md':
        return '1x';
      case 'lg':
        return '1.25x';
      default:
        return '1x';
    }
  }, [size]);

  return (
    <AriaLink
      className={[
        link({ size: baseSize, underline, inline }),
        focusStyleVariants({ focusStyle }),
        !inline ? foregroundColorVariants({ foregroundColor }) : undefined,
        ...sizeResponsiveClasses,
      ]
        .filter(Boolean)
        .join(' ')}
      href={href}
      target={target}
      rel={isExternalLink ? 'noopener noreferrer' : undefined}
      ref={anchorRef}
      {...props}
    >
      {iconStartProps && (
        <Icon
          className={link({ iconStart: !!iconStartProps })}
          iconSize={iconSize}
          {...iconStartProps}
        />
      )}
      {children}
      {iconEndProps && (
        <Icon
          className={link({ iconEnd: !!iconEndProps })}
          iconSize={iconSize}
          {...iconEndProps}
        />
      )}
      {isExternalLink && (
        <VisuallyHidden>
          {externalLinkScreenReaderText || ariaLabelExternal}
        </VisuallyHidden>
      )}
    </AriaLink>
  );
});

export default Link;
